import React, { forwardRef, useMemo } from 'react';

import { type VariantProps, tv } from 'tailwind-variants';

import { cn } from 'common/style/utils';
import { Separator } from 'common/uiV3/Separator';

const FieldSet = forwardRef<HTMLFieldSetElement, React.ComponentPropsWithoutRef<'fieldset'>>(
  ({ className, ...props }, ref) => {
    return (
      <fieldset
        ref={ref}
        data-slot="field-set"
        className={cn(
          'flex flex-col gap-6',
          'has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3',
          className,
        )}
        {...props}
      />
    );
  },
);

const FieldLegend = forwardRef<
  HTMLLegendElement,
  React.ComponentPropsWithoutRef<'legend'> & { variant?: 'legend' | 'label' }
>(({ className, variant = 'legend', ...props }, ref) => {
  return (
    <legend
      ref={ref}
      data-slot="field-legend"
      data-variant={variant}
      className={cn(
        'mb-3 font-medium',
        'data-[variant=legend]:text-base',
        'data-[variant=label]:text-sm',
        className,
      )}
      {...props}
    />
  );
});

const FieldGroup = forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-slot="field-group"
        className={cn(
          'box-border group/field-group @container/field-group flex w-full flex-col gap-7 data-[slot=checkbox-group]:gap-3 [&>[data-slot=field-group]]:gap-4',
          className,
        )}
        {...props}
      />
    );
  },
);

const fieldVariants = tv({
  base: 'group/field flex w-full gap-2 data-[invalid=true]:text-destructive',
  variants: {
    orientation: {
      vertical: ['flex-col [&>*]:w-full [&>.sr-only]:w-auto'],
      horizontal: [
        'flex-row items-center',
        '[&>[data-slot=field-label]]:flex-auto',
        'has-[>[data-slot=field-content]]:items-start has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
      ],
      responsive: [
        'flex-col [&>*]:w-full [&>.sr-only]:w-auto @md/field-group:flex-row @md/field-group:items-center @md/field-group:[&>*]:w-auto',
        '@md/field-group:[&>[data-slot=field-label]]:flex-auto',
        '@md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
      ],
    },
  },
  defaultVariants: {
    orientation: 'vertical',
  },
});

const FieldRoot = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'> & VariantProps<typeof fieldVariants>
>(({ className, orientation = 'vertical', ...props }, ref) => {
  return (
    <div
      ref={ref}
      role="group"
      data-slot="field"
      data-orientation={orientation}
      className={cn(fieldVariants({ orientation }), className)}
      {...props}
    />
  );
});
FieldRoot.displayName = 'Field';

const FieldContent = forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-slot="field-content"
        className={cn('group/field-content flex flex-1 flex-col gap-1.5 leading-snug', className)}
        {...props}
      />
    );
  },
);
FieldContent.displayName = 'FieldContent';

const FieldLabel = forwardRef<
  HTMLLabelElement,
  React.ComponentPropsWithoutRef<'label'> & { required?: boolean }
>(({ className, required, children, ...props }, ref) => {
  return (
    <label
      ref={ref}
      data-slot="field-label"
      className={cn(
        'group/field-label text-base font-medium peer/field-label flex w-fit leading-snug group-data-[disabled=true]/field:opacity-50',
        'has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col has-[>[data-slot=field]]:rounded-md has-[>[data-slot=field]]:border [&>*]:data-[slot=field]:p-4',
        'has-data-[state=checked]:bg-primary/5 has-data-[state=checked]:border-primary dark:has-data-[state=checked]:bg-primary/10',
        {
          'after:content-["*"] after:text-destructive after:ml-0.5': required,
        },
        className,
      )}
      {...props}>
      {children}
    </label>
  );
});
FieldLabel.displayName = 'FieldLabel';

const FieldTitle = forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-slot="field-label"
        className={cn(
          'flex w-fit items-center gap-2 text-sm leading-snug font-medium group-data-[disabled=true]/field:opacity-50',
          className,
        )}
        {...props}
      />
    );
  },
);
FieldTitle.displayName = 'FieldTitle';

const FieldDescription = forwardRef<HTMLParagraphElement, React.ComponentPropsWithoutRef<'p'>>(
  ({ className, ...props }, ref) => {
    return (
      <p
        ref={ref}
        data-slot="field-description"
        className={cn(
          'text-secondary-foreground text-sm leading-normal font-normal group-has-[[data-orientation=horizontal]]/field:text-balance',
          'last:mt-0 [[data-variant=legend]+&]:-mt-1.5',
          '[&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4',
          className,
        )}
        {...props}
      />
    );
  },
);
FieldDescription.displayName = 'FieldDescription';

const FieldSeparator = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'> & {
    children?: React.ReactNode;
  }
>(({ children, className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-slot="field-separator"
      data-content={!!children}
      className={cn(
        'relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2',
        className,
      )}
      {...props}>
      <Separator className="absolute inset-0 top-1/2" />
      {children && (
        <span
          className="bg-background text-muted-foreground relative mx-auto block w-fit px-2"
          data-slot="field-separator-content">
          {children}
        </span>
      )}
    </div>
  );
});
FieldSeparator.displayName = 'FieldSeparator';

const FieldError = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'> & {
    errors?: Array<{ message?: string } | undefined>;
  }
>(({ className, children, errors, ...props }, ref) => {
  const content = useMemo(() => {
    if (children) {
      return children;
    }

    if (!errors) {
      return null;
    }

    if (errors?.length === 1 && errors[0]?.message) {
      return errors[0].message;
    }

    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {errors.map((error, index) => error?.message && <li key={index}>{error.message}</li>)}
      </ul>
    );
  }, [children, errors]);

  if (!content) {
    return null;
  }

  return (
    <div
      ref={ref}
      role="alert"
      data-slot="field-error"
      className={cn('text-destructive text-sm font-normal', className)}
      {...props}>
      {content}
    </div>
  );
});
FieldError.displayName = 'FieldError';

const Field = Object.assign(FieldRoot, {
  Content: FieldContent,
  Description: FieldDescription,
  Error: FieldError,
  Group: FieldGroup,
  Label: FieldLabel,
  Legend: FieldLegend,
  Separator: FieldSeparator,
  Set: FieldSet,
  Title: FieldTitle,
});

export default Field;
